export type AnswersSaveBookLevel = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

export type AnswersSaveConfig = {
  level: AnswersSaveBookLevel;
  bookId: number;
  bookTitle: string;
  fileName: string;
  email?: string | null;
  userId?: string | null;
};

const ANSWERS_SAVE_SCRIPT_ID = "dear-language-answers-save-script";

const cleanText = (value?: string | number | null): string => {
  return String(value ?? "").trim();
};

const cleanEmail = (value?: string | null): string => {
  return cleanText(value).toLowerCase();
};

const cleanUserId = (value?: string | null): string => {
  return cleanText(value);
};

const safeBookId = (value: number): number => {
  return Number.isInteger(value) && value >= 1 && value <= 10 ? value : 1;
};

const safeScriptJson = (value: unknown): string => {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
};

export const getAnswersStorageUserKey = (
  email?: string | null,
  userId?: string | null,
): string => {
  return cleanEmail(email) || cleanUserId(userId) || "guest";
};

export const getAnswersStorageKey = (config: AnswersSaveConfig): string => {
  const userKey = getAnswersStorageUserKey(config.email, config.userId);

  return `dear-language-book-answers-v1:${userKey}:${config.level}:${safeBookId(
    config.bookId,
  )}`;
};

export const buildAnswersSaveScript = (config: AnswersSaveConfig): string => {
  const bookId = safeBookId(config.bookId);
  const level = config.level;
  const userKey = getAnswersStorageUserKey(config.email, config.userId);
  const normalizedConfig = {
    version: 1,
    app: "dear-language",
    storagePrefix: "dear-language-book-answers-v1",
    userKey,
    email: cleanEmail(config.email),
    userId: cleanUserId(config.userId),
    level,
    bookId,
    bookTitle: cleanText(config.bookTitle),
    fileName: cleanText(config.fileName),
    primaryKey: getAnswersStorageKey(config),
    lastKey: `dear-language-book-answers-v1:last:${level}:${bookId}`,
    legacyKeys: [
      `deutsch_${level.toLowerCase()}_${bookId}_answers_v2`,
      `deutsch_${level.toLowerCase()}_${bookId}_answers_v1`,
      `deutsch_${level.toLowerCase()}_${bookId}_answers`,
    ],
  };

  return `(function(){
'use strict';
if(window.__DEAR_LANGUAGE_ANSWERS_SAVE_ACTIVE__){return;}
window.__DEAR_LANGUAGE_ANSWERS_SAVE_ACTIVE__=true;

var CONFIG=${safeScriptJson(normalizedConfig)};
var FIELD_SELECTOR=[
  'textarea.qf',
  'textarea[data-answer]',
  'textarea[aria-label]',
  'input:not([type])',
  'input[type="text"]',
  'input[type="search"]',
  'input[type="email"]',
  'input[type="number"]',
  'au',
  'zp',
  '[contenteditable="true"][role="textbox"]',
  '[contenteditable="true"][aria-label]'
].join(',');
var saveTimer=null;
var restoreTimer=null;
var MEMORY_PAYLOAD=null;

function now(){return Date.now?Date.now():(new Date()).getTime();}
function isObject(value){return !!value&&typeof value==='object'&&!Array.isArray(value);}
function normalizeText(value){return String(value==null?'':value).replace(/\\s+/g,' ').trim();}
function safeJsonParse(value){try{return JSON.parse(value||'');}catch(error){return null;}}
function toArray(list){return Array.prototype.slice.call(list||[]);}
function matches(element,selector){
  if(!element||element.nodeType!==1){return false;}
  var fn=element.matches||element.msMatchesSelector||element.webkitMatchesSelector;
  return !!(fn&&fn.call(element,selector));
}
function closest(element,selector){
  var node=element&&element.nodeType===1?element:element&&element.parentElement;
  while(node&&node.nodeType===1){
    if(matches(node,selector)){return node;}
    node=node.parentElement;
  }
  return null;
}
function storageGet(key){
  var value=null;
  try{
    value=window.localStorage.getItem(key);
    if(value!==null&&value!==undefined){return value;}
  }catch(error){}
  try{return window.sessionStorage.getItem(key);}catch(error){}
  return null;
}
function storageSet(key,value){
  try{window.localStorage.setItem(key,value);return true;}catch(error){}
  try{window.sessionStorage.setItem(key,value);return true;}catch(error){}
  return false;
}
function normalizePayload(value){
  if(!isObject(value)){return null;}
  var answers=isObject(value.answers)?value.answers:value;
  return {
    version:1,
    app:CONFIG.app,
    saved_at:Number(value.saved_at||now()),
    updated_at:Number(value.updated_at||value.saved_at||now()),
    userKey:CONFIG.userKey,
    email:CONFIG.email,
    userId:CONFIG.userId,
    level:CONFIG.level,
    bookId:CONFIG.bookId,
    bookTitle:CONFIG.bookTitle,
    fileName:CONFIG.fileName,
    answers:answers
  };
}
function emptyPayload(){
  return {
    version:1,
    app:CONFIG.app,
    saved_at:now(),
    updated_at:now(),
    userKey:CONFIG.userKey,
    email:CONFIG.email,
    userId:CONFIG.userId,
    level:CONFIG.level,
    bookId:CONFIG.bookId,
    bookTitle:CONFIG.bookTitle,
    fileName:CONFIG.fileName,
    answers:{}
  };
}
function readPayload(){
  var payload=normalizePayload(safeJsonParse(storageGet(CONFIG.primaryKey)))||normalizePayload(safeJsonParse(storageGet(CONFIG.lastKey)))||emptyPayload();
  payload.answers=isObject(payload.answers)?payload.answers:{};

  for(var i=0;i<CONFIG.legacyKeys.length;i+=1){
    var legacy=normalizePayload(safeJsonParse(storageGet(CONFIG.legacyKeys[i])));
    if(!legacy||!isObject(legacy.answers)){continue;}
    Object.keys(legacy.answers).forEach(function(key){
      if(payload.answers[key]===undefined){payload.answers[key]=legacy.answers[key];}
    });
  }

  if(MEMORY_PAYLOAD&&isObject(MEMORY_PAYLOAD.answers)){
    Object.keys(MEMORY_PAYLOAD.answers).forEach(function(key){
      if(payload.answers[key]===undefined){payload.answers[key]=MEMORY_PAYLOAD.answers[key];}
    });
  }

  return payload;
}
function writePayload(payload){
  payload.version=1;
  payload.app=CONFIG.app;
  payload.userKey=CONFIG.userKey;
  payload.email=CONFIG.email;
  payload.userId=CONFIG.userId;
  payload.level=CONFIG.level;
  payload.bookId=CONFIG.bookId;
  payload.bookTitle=CONFIG.bookTitle;
  payload.fileName=CONFIG.fileName;
  payload.updated_at=now();
  if(!payload.saved_at){payload.saved_at=payload.updated_at;}
  payload.answers=isObject(payload.answers)?payload.answers:{};

  MEMORY_PAYLOAD=payload;
  var json=JSON.stringify(payload);
  var okPrimary=storageSet(CONFIG.primaryKey,json);
  storageSet(CONFIG.lastKey,json);

  try{
    if(CONFIG.legacyKeys&&CONFIG.legacyKeys[0]){
      storageSet(CONFIG.legacyKeys[0],JSON.stringify(payload.answers));
    }
  }catch(error){}

  return okPrimary;
}
function isAnswerField(element){
  if(!element||element.nodeType!==1){return false;}
  if(closest(element,'.sa,.sb,.ss,button')){return false;}
  if(element.disabled||element.readOnly){return false;}
  var tag=String(element.tagName||'').toLowerCase();
  if(tag==='textarea'){return true;}
  if(tag==='au'||tag==='zp'){return true;}
  if(tag==='input'){
    var type=String(element.getAttribute('type')||'text').toLowerCase();
    return !type||type==='text'||type==='search'||type==='email'||type==='number';
  }
  return String(element.getAttribute('contenteditable')||'').toLowerCase()==='true';
}
function findFields(root){
  return toArray((root||document).querySelectorAll(FIELD_SELECTOR)).filter(isAnswerField);
}
function getFieldIndex(element){
  var all=findFields(document);
  var index=all.indexOf(element);
  return index>=0?index:0;
}
function getLegacyKey(element,index){
  if(element&&element._k){return String(element._k);}
  var existing=element.getAttribute('data-dear-answer-legacy-key')||element.getAttribute('data-answer-key')||'';
  if(existing){return existing;}
  return 'f'+index;
}
function getFieldKeys(element,index){
  var keys=[];
  var legacyKey=getLegacyKey(element,index);
  var explicit=element.getAttribute('data-dear-answer-key')||'';
  var id=element.getAttribute('id')||'';
  var name=element.getAttribute('name')||'';
  var aria=normalizeText(element.getAttribute('aria-label')||'');
  var section=closest(element,'z1');
  var sectionIndex=section?toArray(document.querySelectorAll('z1')).indexOf(section):-1;
  var page=closest(element,'qz[id],[id^="p"]');
  var pageId=page?(page.getAttribute('id')||''):'';
  var tag=String(element.tagName||'field').toLowerCase();

  if(legacyKey){keys.push(legacyKey);}
  if(explicit){keys.push(explicit);}
  if(id){keys.push('id:'+id);}
  if(name){keys.push('name:'+name);}
  if(aria){keys.push('aria:'+aria);}
  keys.push('index:'+index);
  keys.push('field:'+index);
  keys.push('section:'+sectionIndex+':'+tag+':'+index);
  if(pageId){keys.push('page:'+pageId+':'+tag+':'+index);}

  var unique=[];
  keys.forEach(function(key){
    key=String(key||'').trim();
    if(key&&unique.indexOf(key)<0){unique.push(key);}
  });

  if(!element.getAttribute('data-dear-answer-key')){
    element.setAttribute('data-dear-answer-key',unique[0]||('field:'+index));
  }
  if(!element.getAttribute('data-dear-answer-legacy-key')){
    element.setAttribute('data-dear-answer-legacy-key',legacyKey||('f'+index));
  }

  return unique;
}
function getValue(element){
  var tag=String(element.tagName||'').toLowerCase();
  if(tag==='textarea'||tag==='input'){return String(element.value||'');}
  return String(element.textContent||'');
}
function growTextarea(element){
  if(!element||String(element.tagName||'').toLowerCase()!=='textarea'){return;}
  try{
    element.style.setProperty('height','auto','important');
    element.style.setProperty('height',Math.max(element.scrollHeight,18)+'px','important');
  }catch(error){}
}
function setValue(element,value){
  value=String(value==null?'':value);
  var tag=String(element.tagName||'').toLowerCase();
  if(tag==='textarea'||tag==='input'){
    if(element.value!==value){element.value=value;}
    growTextarea(element);
    return;
  }
  if(element.textContent!==value){element.textContent=value;}
}
function restoreAnswers(){
  var payload=readPayload();
  var answers=isObject(payload.answers)?payload.answers:{};
  var fields=findFields(document);

  fields.forEach(function(element,index){
    var keys=getFieldKeys(element,index);
    for(var i=0;i<keys.length;i+=1){
      if(answers[keys[i]]!==undefined){
        setValue(element,answers[keys[i]]);
        element.setAttribute('data-dear-answer-restored','true');
        break;
      }
    }
  });
}
function saveFields(root){
  var payload=readPayload();
  payload.answers=isObject(payload.answers)?payload.answers:{};

  findFields(root||document).forEach(function(element){
    var index=getFieldIndex(element);
    var value=getValue(element);
    getFieldKeys(element,index).forEach(function(key){
      payload.answers[key]=value;
    });
  });

  return writePayload(payload);
}
function resetFields(root){
  var payload=readPayload();
  payload.answers=isObject(payload.answers)?payload.answers:{};

  findFields(root||document).forEach(function(element){
    var index=getFieldIndex(element);
    getFieldKeys(element,index).forEach(function(key){
      delete payload.answers[key];
    });
    setValue(element,'');
  });

  return writePayload(payload);
}
function showMessage(section,text){
  var message=section&&section.querySelector?section.querySelector('.ss'):null;
  if(!message){return;}
  message.textContent=text;
  message.classList.add('sh');
  if(message.__dearHideTimer){clearTimeout(message.__dearHideTimer);}
  message.__dearHideTimer=setTimeout(function(){message.classList.remove('sh');},1400);
}
function scheduleSave(){
  if(saveTimer){clearTimeout(saveTimer);}
  saveTimer=setTimeout(function(){saveFields(document);},450);
}
function scheduleRestore(){
  if(restoreTimer){clearTimeout(restoreTimer);}
  restoreTimer=setTimeout(function(){restoreAnswers();},80);
}
function buttonAction(button){
  if(!button){return '';}
  if(matches(button,'.sv')){return 'save';}
  if(matches(button,'.sr')){return 'reset';}
  var label=(button.getAttribute('aria-label')||button.getAttribute('title')||button.textContent||'').toLowerCase();
  if(label.indexOf('speichern')>=0||label.indexOf('save')>=0||label.indexOf('💾')>=0){return 'save';}
  if(label.indexOf('zurücksetzen')>=0||label.indexOf('zuruecksetzen')>=0||label.indexOf('reset')>=0||label.indexOf('↺')>=0){return 'reset';}
  return '';
}
function init(){
  restoreAnswers();
  setTimeout(restoreAnswers,0);
  setTimeout(restoreAnswers,120);
  setTimeout(restoreAnswers,350);
  setTimeout(restoreAnswers,900);
}

if(document.readyState==='loading'){
  document.addEventListener('DOMContentLoaded',init,false);
}else{
  init();
}
window.addEventListener('load',function(){setTimeout(restoreAnswers,0);setTimeout(restoreAnswers,250);},false);

document.addEventListener('input',function(event){
  var field=closest(event.target,FIELD_SELECTOR);
  if(field&&isAnswerField(field)){scheduleSave();}
},true);
document.addEventListener('change',function(event){
  var field=closest(event.target,FIELD_SELECTOR);
  if(field&&isAnswerField(field)){scheduleSave();}
},true);
document.addEventListener('blur',function(event){
  var field=closest(event.target,FIELD_SELECTOR);
  if(field&&isAnswerField(field)){saveFields(document);}
},true);
document.addEventListener('click',function(event){
  var button=closest(event.target,'.sb,button');
  var action=buttonAction(button);
  if(!action){return;}
  var section=closest(button,'z1')||document;
  setTimeout(function(){
    if(action==='reset'){
      var resetOk=resetFields(section);
      showMessage(section,resetOk?'Zurückgesetzt':'Fehler');
      return;
    }
    var saveOk=saveFields(section);
    showMessage(section,saveOk?'Gespeichert':'Fehler');
  },35);
},true);
window.addEventListener('pagehide',function(){saveFields(document);},false);
window.addEventListener('beforeunload',function(){saveFields(document);},false);
document.addEventListener('visibilitychange',function(){
  if(document.visibilityState==='hidden'){saveFields(document);}
},false);

try{
  var observer=new MutationObserver(function(){scheduleRestore();});
  observer.observe(document.documentElement||document,{childList:true,subtree:true});
}catch(error){}

window.DearLanguageAnswersSave={
  key:CONFIG.primaryKey,
  save:function(){return saveFields(document);},
  restore:restoreAnswers,
  reset:function(){return resetFields(document);},
  read:function(){return readPayload();}
};
})();`;
};

export const buildAnswersSaveScriptTag = (
  config: AnswersSaveConfig,
): string => {
  const script = buildAnswersSaveScript(config).replace(
    /<\/script/gi,
    "<\\/script",
  );

  return `<script id="${ANSWERS_SAVE_SCRIPT_ID}">${script}</script>`;
};

export const injectAnswersSaveScript = (
  html: string,
  config: AnswersSaveConfig,
): string => {
  const cleanHtml = cleanText(html);

  if (!cleanHtml) {
    return "";
  }

  const withoutOldScript = cleanHtml.replace(
    /<script\b[^>]*id=["']dear-language-answers-save-script["'][\s\S]*?<\/script>/gi,
    "",
  );

  const scriptTag = buildAnswersSaveScriptTag(config);

  if (/<\/head>/i.test(withoutOldScript)) {
    return withoutOldScript.replace(/<\/head>/i, `${scriptTag}</head>`);
  }

  if (/<body[\s>]/i.test(withoutOldScript)) {
    return withoutOldScript.replace(/<body([^>]*)>/i, `<body$1>${scriptTag}`);
  }

  return `${scriptTag}${withoutOldScript}`;
};
